import { supabase } from '../supabaseClient';
import { getCachedUserAndProfile } from '../utils/userCache';
import { logAudit } from './auditLog';
import { addAssignee } from './ticketAssignees';
import { updateTicketActivity } from './ticketActivity';
import { syncTicketReferences } from './ticketReferences';

/**
 * 建立新 Ticket
 */
export const createTicket = async (data) => {
  try {
    const insertData = {
      project_id: data.project_id,
      company_id: data.company_id,
      department_id: data.department_id,
      creator_id: data.creator_id,
      title: data.title,
      description: data.description || null,
      status: data.status || 'open',
      priority: data.priority || 1,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      days: data.days || 1,
      display_order: data.display_order || 0,
      completed: data.status === 'completed',
      completed_at: data.status === 'completed' ? new Date().toISOString() : null,
      last_activity_at: new Date().toISOString(),
      last_activity_by: data.creator_id,
    };

    const { data: ticket, error } = await supabase
      .from('tickets')
      .insert(insertData)
      .select('*')
      .single();

    if (error) throw error;

    // Assign selected assignees（逐一呼叫 addAssignee 以觸發通知）
    if (data.assignee_ids?.length > 0) {
      const usersMap = new Map(
        (data.assignee_users || []).map((u) => [u.id, u])
      );
      for (const uid of data.assignee_ids) {
        await addAssignee(ticket.id, uid, data.creator_id, usersMap.get(uid) || null, { skipAudit: true });
      }
    }

    await logAudit('create', 'ticket', ticket.id, ticket.title, {
      after: ticket,
    });

    return { data: ticket, error: null };
  } catch (error) {
    console.error('Error creating ticket:', error);
    return { data: null, error };
  }
};

/**
 * 取得公司下所有 Tickets（不篩指派人，用於視圖頁面）
 */
export const getAllTickets = async ({ companyId = null } = {}) => {
  try {
    let query = supabase
      .from('tickets')
      .select(`
        *,
        projects:project_id (id, name, color),
        ticket_assignees (
          user_id,
          assigned_at,
          profiles:user_id (id, email)
        )
      `)
      .neq('status', 'closed')
      .order('assigned_at', { referencedTable: 'ticket_assignees', ascending: true })
      .order('created_at', { ascending: false });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching all tickets:', error);
    return { data: null, error };
  }
};

/**
 * 取得 Project 下的所有 Tickets
 */
export const getTicketsByProject = async (projectId, { showCompleted = false } = {}) => {
  try {
    let query = supabase
      .from('tickets')
      .select(`
        *,
        ticket_assignees (
          user_id,
          assigned_at,
          profiles:user_id (id, email)
        )
      `)
      .eq('project_id', projectId)
      .order('assigned_at', { referencedTable: 'ticket_assignees', ascending: true })
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (!showCompleted) {
      query = query.neq('status', 'closed');
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching tickets:', error);
    return { data: null, error };
  }
};

/**
 * 取得指派給特定使用者的所有 Tickets
 */
export const getTicketsForUser = async (userId, { showCompleted = false, companyId = null } = {}) => {
  try {
    // Get ticket IDs assigned to this user
    const { data: assigneeData, error: assigneeError } = await supabase
      .from('ticket_assignees')
      .select('ticket_id')
      .eq('user_id', userId);

    if (assigneeError) throw assigneeError;

    if (!assigneeData || assigneeData.length === 0) {
      return { data: [], error: null };
    }

    const ticketIds = assigneeData.map((a) => a.ticket_id);

    let query = supabase
      .from('tickets')
      .select(`
        *,
        projects:project_id (id, name, color),
        ticket_assignees (
          user_id,
          profiles:user_id (id, email)
        )
      `)
      .in('id', ticketIds)
      .order('assigned_at', { referencedTable: 'ticket_assignees', ascending: true })
      .order('created_at', { ascending: false });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    if (!showCompleted) {
      query = query.neq('status', 'closed');
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching user tickets:', error);
    return { data: null, error };
  }
};

/**
 * 取得單一 Ticket（含完整關聯資料）
 */
export const getTicket = async (ticketId) => {
  try {
    const { data, error } = await supabase
      .from('tickets')
      .select(`
        *,
        projects:project_id (id, name, color),
        ticket_assignees (
          user_id,
          assigned_at,
          assigned_by,
          profiles:user_id (id, email)
        ),
        creator:creator_id (id, email)
      `)
      .eq('id', ticketId)
      .order('assigned_at', { referencedTable: 'ticket_assignees', ascending: true })
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching ticket:', error);
    return { data: null, error };
  }
};

/**
 * 用 ticket_no（公司內流水號）查詢 ticket ID
 */
export const getTicketByNo = async (ticketNo, companyId) => {
  try {
    const { data, error } = await supabase
      .from('tickets')
      .select('id, project_id')
      .eq('ticket_no', ticketNo)
      .eq('company_id', companyId)
      .maybeSingle();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * 更新 Ticket
 */
export const updateTicket = async (ticketId, updates) => {
  try {
    const { data: beforeData } = await supabase
      .from('tickets')
      .select('*')
      .eq('id', ticketId)
      .single();

    const updateData = {};

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.start_date !== undefined) updateData.start_date = updates.start_date;
    if (updates.end_date !== undefined) updateData.end_date = updates.end_date;
    if (updates.days !== undefined) updateData.days = updates.days;
    if (updates.display_order !== undefined) updateData.display_order = updates.display_order;
    if (updates.project_id !== undefined) updateData.project_id = updates.project_id;

    // Handle status change with completed_at logic
    if (updates.status !== undefined) {
      updateData.status = updates.status;
      if (updates.status === 'completed') {
        updateData.completed = true;
        updateData.completed_at = new Date().toISOString();
      } else {
        updateData.completed = false;
        updateData.completed_at = null;
      }
    }

    if (updates.completed !== undefined && updates.status === undefined) {
      updateData.completed = updates.completed;
      if (updates.completed) {
        updateData.completed_at = new Date().toISOString();
        updateData.status = 'completed';
      } else {
        updateData.completed_at = null;
      }
    }

    updateData.updated_at = new Date().toISOString();

    // 同時更新 last_activity（不再依賴 trigger）
    const { profile } = await getCachedUserAndProfile(supabase);
    updateData.last_activity_at = updateData.updated_at;
    if (profile?.id) {
      updateData.last_activity_by = profile.id;
    }

    const { data, error } = await supabase
      .from('tickets')
      .update(updateData)
      .eq('id', ticketId)
      .select()
      .single();

    if (error) throw error;

    // 狀態變更 → 記 status_change（timeline 可見）；其他 → 記 update
    if (updates.status !== undefined && beforeData.status !== data.status) {
      await logAudit('status_change', 'ticket', ticketId, data.title, {
        before: beforeData,
        after: data,
      }, {
        old_status: beforeData.status,
        new_status: data.status,
      });
    } else {
      await logAudit('update', 'ticket', ticketId, data.title, {
        before: beforeData,
        after: data,
      });
    }

    // 描述變更時同步 ticket references（fire-and-forget）
    if (updates.description !== undefined) {
      syncTicketReferences(ticketId, beforeData?.company_id, profile?.id);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating ticket:', error);
    return { data: null, error };
  }
};

/**
 * 統一搜尋 Tickets（server-side 分頁 + 篩選）
 *
 * @param {Object} params
 * @param {number|null} params.projectId - project 模式
 * @param {string|null} params.userId - my-tasks 模式
 * @param {string} params.search - 搜尋 title / description
 * @param {string[]} params.status - 狀態篩選（多選）
 * @param {number[]} params.priorities - 優先度篩選（多選）
 * @param {string[]} params.assigneeIds - 指派人篩選（多選，OR 邏輯）
 * @param {number} params.page - 頁碼
 * @param {number} params.pageSize - 每頁筆數
 * @param {string} params.sortBy - 排序欄位
 * @param {string} params.sortOrder - asc | desc
 * @returns {{ data: Array, count: number, error: any }}
 */
export const searchTickets = async ({
  projectId = null,
  projectIds = [],
  userId = null,
  companyId = null,
  search = '',
  status = [],
  priorities = [],
  assigneeIds = [],
  extendedSearch = false,
  page = 1,
  pageSize = 20,
  sortBy = 'ticket_no',
  sortOrder = 'desc',
} = {}) => {
  try {
    // ── 1. 先確定可查詢的 ticket IDs 範圍 ──
    let scopeTicketIds = null;

    if (userId && !projectId) {
      // my-tasks 模式：先查 ticket_assignees
      const { data: assigneeData, error: aErr } = await supabase
        .from('ticket_assignees')
        .select('ticket_id')
        .eq('user_id', userId);
      if (aErr) throw aErr;
      scopeTicketIds = (assigneeData || []).map((a) => a.ticket_id);
      if (scopeTicketIds.length === 0) {
        return { data: [], count: 0, error: null };
      }
    }

    // ── 2. 指派人篩選（多選 OR）：取得符合的 ticket IDs ──
    let assigneeTicketIds = null;
    if (assigneeIds.length > 0) {
      const { data: aData, error: aErr } = await supabase
        .from('ticket_assignees')
        .select('ticket_id')
        .in('user_id', assigneeIds);
      if (aErr) throw aErr;
      assigneeTicketIds = [...new Set((aData || []).map((a) => a.ticket_id))];
      if (assigneeTicketIds.length === 0) {
        return { data: [], count: 0, error: null };
      }
    }

    // ── 3. 交集 scope 與 assignee IDs ──
    let finalIds = null;
    if (scopeTicketIds && assigneeTicketIds) {
      const set = new Set(assigneeTicketIds);
      finalIds = scopeTicketIds.filter((id) => set.has(id));
      if (finalIds.length === 0) return { data: [], count: 0, error: null };
    } else if (scopeTicketIds) {
      finalIds = scopeTicketIds;
    } else if (assigneeTicketIds) {
      finalIds = assigneeTicketIds;
    }

    // ── 3.5 跨表搜尋（extendedSearch）：comment + todo 內容匹配 ──
    let extendedTicketIds = null;
    const searchTerm = search.trim();
    const isIdSearch = searchTerm && /^#\d+$/.test(searchTerm);
    if (extendedSearch && searchTerm && !isIdSearch) {
      const term = `%${searchTerm}%`;
      const [commentResult, todoResult] = await Promise.all([
        supabase
          .from('ticket_comments')
          .select('ticket_id')
          .ilike('content', term),
        supabase
          .from('items')
          .select('ticket_id')
          .not('ticket_id', 'is', null)
          .or(`title.ilike.${term},description.ilike.${term}`),
      ]);
      const commentIds = (commentResult.data || []).map((r) => r.ticket_id);
      const todoIds = (todoResult.data || []).map((r) => r.ticket_id);
      extendedTicketIds = [...new Set([...commentIds, ...todoIds])];
    }

    // ── 4. 建構主查詢 ──
    let query = supabase
      .from('tickets')
      .select(
        `
        *,
        projects:project_id (id, name, color),
        ticket_assignees (
          user_id,
          profiles:user_id (id, email)
        )
      `,
        { count: 'exact' }
      );

    // 公司過濾（super admin 切換公司時需要）
    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    // project 模式
    if (projectId) {
      query = query.eq('project_id', projectId);
    } else if (projectIds.length > 0) {
      query = query.in('project_id', projectIds);
    }

    // 限定 ticket IDs 範圍
    if (finalIds) {
      query = query.in('id', finalIds);
    }

    // 搜尋：#123 精確匹配 ticket_no（公司內流水號），其他走標題/描述模糊搜尋
    if (searchTerm) {
      if (isIdSearch) {
        query = query.eq('ticket_no', Number(searchTerm.slice(1)));
      } else {
        const term = `%${searchTerm}%`;
        if (extendedTicketIds && extendedTicketIds.length > 0) {
          // 標題/描述 OR 跨表匹配的 ticket IDs
          query = query.or(`title.ilike.${term},description.ilike.${term},id.in.(${extendedTicketIds.join(',')})`);
        } else {
          query = query.or(`title.ilike.${term},description.ilike.${term}`);
        }
      }
    }

    // 狀態篩選：有指定 → 只顯示指定的；沒指定 → 預設排除 closed
    if (status.length > 0) {
      query = query.in('status', status);
    } else {
      query = query.neq('status', 'closed');
    }

    // 優先度（多選 inclusive）
    if (priorities.length > 0) {
      query = query.in('priority', priorities);
    }

    // 嵌套 assignees 排序（先加入 = Owner）
    query = query.order('assigned_at', { referencedTable: 'ticket_assignees', ascending: true });

    // 排序
    const ascending = sortOrder === 'asc';
    query = query.order(sortBy, { ascending });

    // 分頁
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, count, error } = await query;
    if (error) throw error;

    return { data: data || [], count: count || 0, error: null };
  } catch (error) {
    console.error('Error searching tickets:', error);
    return { data: [], count: 0, error };
  }
};

/**
 * DailyReport 專用：取得指定成員的 tickets（含 project 資訊）
 * - 目前被指派的 tickets + 今天被 unassign 的 tickets
 * - 已完成/closed 的只保留「今日 unassign」的
 *
 * @param {string} userId - 成員 ID
 * @param {object} options
 * @param {string|null} options.companyId - 公司 ID
 * @param {string[]} options.todayUnassignedTicketIds - 今天被取消指派的 ticket IDs
 */
export const getTicketsForDailyReport = async (userId, {
  companyId = null,
  todayUnassignedTicketIds = [],
} = {}) => {
  try {
    // 1. 查出該成員目前被指派的 ticket IDs
    const { data: assigneeData, error: assigneeError } = await supabase
      .from('ticket_assignees')
      .select('ticket_id')
      .eq('user_id', userId);

    if (assigneeError) throw assigneeError;

    const assignedTicketIds = (assigneeData || []).map(a => a.ticket_id);
    const allRelevantIds = [...new Set([...assignedTicketIds, ...todayUnassignedTicketIds])];

    if (allRelevantIds.length === 0) {
      return { data: [], error: null };
    }

    // 2. 一次查出所有 tickets（含 project join + assignees，用於顯示 Owner）
    let query = supabase
      .from('tickets')
      .select(`
        *,
        projects:project_id (id, name, color, icon, external_url, description),
        ticket_assignees (
          user_id,
          assigned_at,
          profiles:user_id (id, email)
        )
      `)
      .in('id', allRelevantIds)
      .order('assigned_at', { referencedTable: 'ticket_assignees', ascending: true });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data: tickets, error } = await query;
    if (error) throw error;

    // 3. 過濾：closed 不顯示，除非是今天 closed 或今日被 unassign 的
    //    規則：目前被指派的全部 ticket + 今日被 unassign 的 ticket
    //         closed 只在「今天 closed」或「今日 unassign」時才顯示
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const todayUnassignedSet = new Set(todayUnassignedTicketIds);
    const filtered = (tickets || []).filter(ticket => {
      if (ticket.status !== 'closed') return true;
      // closed：今日 unassign 的保留
      if (todayUnassignedSet.has(ticket.id)) return true;
      // closed：今天 closed 的保留（用 updated_at 判斷）
      const closedDate = ticket.updated_at?.slice(0, 10);
      return closedDate === today;
    });

    return { data: filtered, error: null };
  } catch (error) {
    console.error('Error fetching tickets for daily report:', error);
    return { data: null, error };
  }
};

/**
 * 刪除 Ticket
 */
export const deleteTicket = async (ticketId) => {
  try {
    const { data: ticketData } = await supabase
      .from('tickets')
      .select('title, company_id, department_id')
      .eq('id', ticketId)
      .single();

    const { error } = await supabase
      .from('tickets')
      .delete()
      .eq('id', ticketId);

    if (error) throw error;

    await logAudit('delete', 'ticket', ticketId, ticketData?.title, {
      before: ticketData,
    });

    return { error: null };
  } catch (error) {
    console.error('Error deleting ticket:', error);
    return { error };
  }
};

/**
 * 記錄使用者檢視 ticket 的時間（UPSERT）
 */
export const upsertTicketView = async (ticketId, userId) => {
  const { error } = await supabase
    .from('ticket_views')
    .upsert(
      { ticket_id: ticketId, user_id: userId, last_viewed_at: new Date().toISOString() },
      { onConflict: 'ticket_id,user_id' }
    );
  return { error };
};

/**
 * 批次取得指定 tickets 的 last_viewed_at（供列表判斷未讀狀態）
 */
export const getTicketViews = async (ticketIds, userId) => {
  if (!ticketIds.length || !userId) return { data: [] };
  const { data, error } = await supabase
    .from('ticket_views')
    .select('ticket_id, last_viewed_at')
    .eq('user_id', userId)
    .in('ticket_id', ticketIds);
  if (error) console.error('Error fetching ticket views:', error);
  return { data: data || [] };
};
